import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { BreakdownDataPoint } from 'components/widgets/charts/BreakdownChart';

/*
 * JobStatusBar.tsx
 * Single wide proportional bar showing the open job status
 * breakdown, colored with the same status colors used elsewhere
 * in the app. Clicking a segment opens the job list filtered on
 * its matching status condition and, where relevant, the same
 * date range the bar itself is currently showing.
 */

interface JobStatusBarProps {
    data: BreakdownDataPoint[];
    groupLabels: Record<string, string>;
    loading?: boolean;
    periodFrom: string;
    periodTo: string;
}

// Maps each open werkbonnen bucket to the shared workflow status color variables
const BUCKET_COLOR_VARS: Record<string, string> = {
    to_schedule: 'var(--job_created)',
    ordered: 'var(--one_open_purchase)',
    reserved: 'var(--one_reserved_appointment)',
    scheduled: 'var(--scheduled_appointment)',
    en_route: 'var(--running_travel_timer)',
    in_progress: 'var(--running_work_timer)',
    incomplete: 'var(--incomplete)',
    completed: 'var(--completed)',
};

// Maps each bucket to the status conditions used to filter the job list.
// Mirrors the buckets counted by the backends job status breakdown endpoint
const BUCKET_STATUS_CONDITIONS: Record<string, string[]> = {
    to_schedule: ['job_created', 'to_schedule', 'all_purchases_delivered'],
    ordered: ['one_open_purchase'],
    reserved: ['one_reserved_appointment'],
    scheduled: ['one_scheduled_appointment'],
    en_route: ['running_travel_timer'],
    in_progress: ['running_work_timer'],
    incomplete: ['incomplete'],
    completed: ['completed'],
};

// Which date field each bucket is bound to, matching the backends counting logic.
// Buckets left out here are never date bound and always show every matching job
const BUCKET_DATE_FILTER_TYPE: Record<string, 'target_date' | 'appointment'> = {
    to_schedule: 'target_date',
    ordered: 'target_date',
    reserved: 'appointment',
    scheduled: 'appointment',
};

const JobStatusBar: React.FC<JobStatusBarProps> = ({ data, groupLabels, loading, periodFrom, periodTo }) => {
    const { t } = useTranslation();
    const history = useHistory();

    // Sum the total and only show the buckets which actually contain jobs
    const total = data.reduce((sum, point) => sum + point.value, 0);
    const segments = data.filter(point => point.value > 0);

    // Navigate to the job list, filtered on the status condition and, if relevant, the current period
    const navigateToBucket = (group: string) => {
        const statusConditions = BUCKET_STATUS_CONDITIONS[group];

        if (!statusConditions) {
            return;
        }

        const params = new URLSearchParams({ status_condition: statusConditions.join(',') });
        const dateFilterType = BUCKET_DATE_FILTER_TYPE[group];

        if (dateFilterType === 'target_date') {
            params.set('target_date__gte', periodFrom);
            params.set('target_date__lte', periodTo);
        } else if (dateFilterType === 'appointment') {
            params.set('appointments__start_datetime__gte', periodFrom);
            params.set('appointments__start_datetime__lte', periodTo);
        }

        history.push(`/jobs?${params.toString()}`);
    };

    return (
        <div className='dashboard-card job-status-bar-card'>
            <h6 className='title'>{t('dashboard.chart.job_status_breakdown.title')}</h6>
            {loading ? (
                // Show a loader while the data is being fetched
                <div className='metric-chart-loading'>
                    <div className='loader' />
                </div>
            ) : total === 0 ? (
                // Show an empty state when there are no jobs at all
                <div className='metric-chart-empty'>{t('general.no_data_available')}</div>
            ) : (
                <>
                    <div className='job-status-bar'>
                        {segments.map(point => (
                            <div
                                key={point.group}
                                className='job-status-bar-segment tooltip-icon no-drag clickable'
                                style={{ width: `${(point.value / total) * 100}%`, backgroundColor: BUCKET_COLOR_VARS[point.group] }}
                                onClick={() => navigateToBucket(point.group)}
                            >
                                <span className='tooltip'>{groupLabels[point.group]} ({point.value})</span>
                            </div>
                        ))}
                    </div>
                    <div className='job-status-bar-labels'>
                        {segments.map(point => (
                            <div
                                key={point.group}
                                className='job-status-bar-label no-drag clickable'
                                onClick={() => navigateToBucket(point.group)}
                            >
                                <span className='dot' style={{ backgroundColor: BUCKET_COLOR_VARS[point.group] }} />
                                <span>{groupLabels[point.group]} ({point.value})</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default JobStatusBar;
